/* Tree Naturals HQ routine cart v1.9 — separate-line cart adapter.
 * Approved path: Bar Duo + Hair Milk OR Leave-In + optional Hair Growth Oil.
 * No normal Wash Day Essentials SKU substitution. No retries after uncertain writes. */
(function(root){
  'use strict';

  const HANDLES = Object.freeze({
    shampoo: 'bamboo-lemongrass-shampoo-bar',
    conditioner: 'detangling-conditioner-bar',
    treeo: 'tree-o-bundle'
  });

  const idString = value => value === null || value === undefined ? '' : String(value);
  const cleanRoot = value => {
    const v = typeof value === 'string' && value ? value : '/';
    return v.endsWith('/') ? v : `${v}/`;
  };

  class Session {
    constructor(options = {}) {
      this.fetch = options.fetch;
      this.root = cleanRoot(options.root);
      this.currency = options.currency || 'USD';
      this.products = options.products || {};
      this.enabled = options.enabled === true;
      this.ocuVerified = options.ocuVerified === true;
      this.pricingApproved = options.pricingApproved === true;
      this.privateQa = options.privateQa === true;
      this.inFlight = false;
      this.ready = Boolean(
        typeof this.fetch === 'function' && this.pricingApproved &&
        (this.privateQa || (this.enabled && this.ocuVerified))
      );
    }

    variants(key) {
      return Array.isArray(this.products?.[key]?.variants) ? this.products[key].variants : [];
    }

    variant(key, id) {
      const target = idString(id);
      return this.variants(key).find(v => idString(v.id) === target) || null;
    }

    selectedVariants(selected = []) {
      const rows = [];
      for (const item of Array.isArray(selected) ? selected : []) {
        if (!item || !['bars','milk','leavein','oil'].includes(item.key)) continue;
        const variant = this.variant(item.key, item.id);
        rows.push({ key:item.key, id:idString(item.id), variant });
      }
      return rows;
    }

    async requestJson(url, options) {
      const response = await this.fetch(url, options);
      if (!response || !response.ok) {
        let detail = '';
        try { detail = await response.text(); } catch (_) {}
        const error = new Error(detail || `Request failed${response ? ` (${response.status})` : ''}.`);
        error.httpStatus = response?.status || 0;
        throw error;
      }
      return response.json();
    }

    async getCart() {
      return this.requestJson(`${this.root}cart.js`, {
        method:'GET',
        headers:{'Accept':'application/json'},
        credentials:'same-origin'
      });
    }

    cartItems(cart) {
      return Array.isArray(cart?.items) ? cart.items : [];
    }

    cartHasVariant(cart, id) {
      const target = idString(id);
      return this.cartItems(cart).some(item => idString(item.variant_id ?? item.id) === target && Number(item.quantity || 0) > 0);
    }

    cartHasHandle(cart, handle) {
      return this.cartItems(cart).some(item => String(item.handle || '').replace(/^\//,'') === handle && Number(item.quantity || 0) > 0);
    }

    cartHasAnyVariant(cart, key) {
      const ids = new Set(this.variants(key).map(v => idString(v.id)));
      return this.cartItems(cart).some(item => ids.has(idString(item.variant_id ?? item.id)) && Number(item.quantity || 0) > 0);
    }

    cartHasAlternateVariant(cart, key, selectedId) {
      const target = idString(selectedId);
      const ids = new Set(this.variants(key).map(v => idString(v.id)).filter(Boolean));
      return this.cartItems(cart).some(item => {
        const id = idString(item.variant_id ?? item.id);
        return ids.has(id) && id !== target && Number(item.quantity || 0) > 0;
      });
    }

    hasUnknownParentStructure(cart) {
      return this.cartItems(cart).some(item => {
        const hasParent = Boolean(item.parent_relationship || item.parentRelationship);
        const hasComponents = Array.isArray(item.components) && item.components.length > 0;
        return hasParent || hasComponents;
      });
    }

    signature(cart, selected) {
      const cartState = this.cartItems(cart).map(item => ({
        id:idString(item.variant_id ?? item.id),
        quantity:Number(item.quantity || 0),
        key:String(item.key || ''),
        handle:String(item.handle || '')
      })).sort((a,b) => `${a.id}|${a.key}`.localeCompare(`${b.id}|${b.key}`));
      const selectedState = this.selectedVariants(selected).map(x => `${x.key}:${x.id}`).sort();
      return JSON.stringify({currency:String(cart?.currency || ''),cartState,selectedState});
    }

    plan(cart, selected) {
      const issues = [];
      const advisories = [];
      const items = [];
      const rows = this.selectedVariants(selected);

      const addIssue = (code, message) => {
        if (!issues.some(x => x.code === code)) issues.push({code,message});
      };
      const addAdvisory = (code, message) => {
        if (!advisories.some(x => x.code === code)) advisories.push({code,message});
      };

      if (cart?.currency && this.currency && String(cart.currency).toUpperCase() !== String(this.currency).toUpperCase()) {
        addIssue('CURRENCY_CHANGED', 'Your cart currency changed. Please review the cart before adding this routine.');
      }

      if (this.hasUnknownParentStructure(cart)) {
        addIssue('BUNDLE_PARENT_REVIEW', 'Your cart contains a grouped product that needs review before this routine can add anything.');
      }

      for (const row of rows) {
        if (!row.variant) addIssue(`UNKNOWN_${row.key.toUpperCase()}`, 'One of your selected options is no longer available. Please choose it again.');
        else if (row.variant.available !== true) addIssue(`UNAVAILABLE_${row.key.toUpperCase()}`, 'One of your selected options is sold out. Please choose another available option.');
      }

      const moistureRows = rows.filter(x => x.key === 'milk' || x.key === 'leavein');
      if (rows.filter(x => x.key === 'bars').length !== 1 || moistureRows.length !== 1 || rows.filter(x => x.key === 'oil').length > 1) {
        addIssue('INVALID_ROUTINE', 'Choose the Bar Duo and one moisture option before adding your routine.');
      }
      const moistureKits = new Set(['8137009353','8159120841','8487593106','4504122458181','6567430357061','6806882484293','6806882517061','9913505415467']);
      if (moistureRows.length && this.cartItems(cart).some(item => moistureKits.has(idString(item.product_id)) && Number(item.quantity || 0) > 0)) {
        addIssue('KIT_MOISTURE_OVERLAP', 'Your cart already contains a kit with Leave-In Conditioner or Hair Milk. Review it before adding another moisture step.');
      }
      if (moistureRows.some(row => this.cartHasAlternateVariant(cart, row.key, row.id))) {
        addIssue('MOISTURE_SIZE_OVERLAP', 'Your cart already contains another size of this moisturizer. Review it before adding a second bottle.');
      }

      const wantsBars = rows.some(x => x.key === 'bars');
      const moistureRow = rows.find(x => x.key === 'milk' || x.key === 'leavein');
      const oilRow = rows.find(x => x.key === 'oil');

      if (wantsBars && (this.cartHasHandle(cart, HANDLES.shampoo) || this.cartHasHandle(cart, HANDLES.conditioner))) {
        addIssue('BAR_COMPONENT_OVERLAP', 'Your cart already contains an individual shampoo or conditioner bar. Review it before adding the Bar Duo.');
      }

      if ((wantsBars || moistureRow) && this.cartHasAnyVariant(cart, 'bundle')) {
        addIssue('WASH_DAY_BUNDLE_OVERLAP', 'Your cart already contains Wash Day Essentials, which overlaps this routine. Review the cart before adding more wash-day or moisture products.');
      }

      if (moistureRow && this.cartHasHandle(cart, HANDLES.treeo)) {
        addIssue('TREEO_MOISTURE_OVERLAP', 'Your cart already contains the Tree-O Kit with its own moisture step. Review the cart before adding another moisturizer.');
      }

      if (moistureRow?.key === 'milk' && this.cartHasAnyVariant(cart, 'leavein')) {
        addIssue('MOISTURE_PATH_OVERLAP', 'Your cart already contains Leave-In Conditioner. Keep one moisture path in this routine before adding Hair Milk.');
      }
      if (moistureRow?.key === 'leavein' && this.cartHasAnyVariant(cart, 'milk')) {
        addIssue('MOISTURE_PATH_OVERLAP', 'Your cart already contains Hair Milk. Keep one moisture path in this routine before adding Leave-In Conditioner.');
      }

      if (oilRow && this.cartHasAlternateVariant(cart, 'oil', oilRow.id)) {
        addIssue('OIL_VARIANT_OVERLAP', 'Your cart already contains another Hair Growth Oil option. Review the size or formula before adding a second one.');
      }

      for (const row of rows) {
        if (!row.variant || row.variant.available !== true) continue;
        if (this.cartHasVariant(cart, row.id)) {
          addAdvisory(`ALREADY_${row.key.toUpperCase()}`, `${row.variant.title || 'That selection'} is already in your cart and will not be duplicated.`);
          continue;
        }
        items.push({id:row.id,quantity:1,key:row.key});
      }

      return {
        ok: issues.length === 0,
        issues,
        advisories,
        lines: rows.map(row => ({key:row.key,id:row.id})),
        items,
        signature:this.signature(cart, selected)
      };
    }

    async snapshot(selected) {
      if (!this.ready) {
        return {status:'gated', message:'The routine cart is not released until its cart and post-purchase checks are verified.'};
      }
      const cart = await this.getCart();
      return {status:'review', cart, plan:this.plan(cart, selected)};
    }

    async commit(selected, priorSignature) {
      if (this.inFlight) return {status: 'busy', message: 'Your routine is already being added.'};
      this.inFlight = true;
      try { return await this.commitOnce(selected, priorSignature); }
      finally { this.inFlight = false; }
    }

    async commitOnce(selected, priorSignature) {
      if (!this.ready) {
        return {status:'gated', message:'The routine cart is not released yet.'};
      }

      let before;
      try {
        before = await this.getCart();
      } catch (_) {
        return {status:'check_cart', message:'We could not re-check your cart. Nothing was submitted.'};
      }

      const plan = this.plan(before, selected);
      if (plan.signature !== priorSignature) {
        return {status:'review_changed', message:'Your cart changed after the review. Please check it again before adding anything.', plan};
      }
      if (!plan.ok) return {status:'blocked', message:'The cart needs review before anything is added.', plan};
      if (!plan.items.length) return {status:'already_complete', message:'The selected routine is already covered by your cart.', cart:before, plan};

      try {
        await this.requestJson(`${this.root}cart/add.js`, {
          method:'POST',
          headers:{'Accept':'application/json','Content-Type':'application/json'},
          credentials:'same-origin',
          body:JSON.stringify({items:plan.items.map(item => ({id:item.id,quantity:1}))})
        });
      } catch (error) {
        if (error?.httpStatus) {
          return {status:'blocked', message:'Shopify did not accept the cart update. Nothing will be retried automatically.', issues:[{code:'ADD_REJECTED',message:'Please review availability in your cart before trying again.'}]};
        }
        return {status:'uncertain', message:'The cart update could not be confirmed. Please check your cart before trying again; nothing will be retried automatically.'};
      }

      let after;
      try {
        after = await this.getCart();
      } catch (_) {
        return {status:'uncertain', message:'The cart update was submitted but the result could not be verified. Please check your cart before doing anything else.'};
      }

      const missing = plan.items.filter(item => !this.cartHasVariant(after, item.id));
      if (missing.length) {
        return {status:'check_cart', message:'Shopify returned a cart that did not contain every reviewed item. Please check the cart before trying again.', cart:after};
      }

      return {status:'added', message:'Your reviewed routine was added to the cart.', cart:after, plan};
    }
  }

  const plan = (cart, selected, options = {}) => new Session({...options,fetch:options.fetch || (() => Promise.reject(new Error('No fetch'))),enabled:true,ocuVerified:true,pricingApproved:true}).plan(cart, selected);
  root.TNRoutineCart = Object.freeze({Session, plan, HANDLES});
})(typeof globalThis !== 'undefined' ? globalThis : this);

